import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from './toast-service.service';
import { GateBlockModalComponent } from 'src/app/shared/gate-block-modal/gate-block-modal.component';

@Injectable({
  providedIn: 'root',
})
export class AlertService {

  constructor(private toastService: ToastService, private modalService: NgbModal) { }


  public showNotificationForHttpError(error: HttpErrorResponse): void {
    // 423 LOCKED — a flagged truck was refused at the gate (MDP Operations, 2026-09-17).
    // Intercepted HERE rather than at the three check-in call sites (store / storePast / update)
    // because all three already funnel through this method: one interception covers every path,
    // including any added later. The API returns 423 rather than 422 precisely so this can be
    // told apart from ordinary validation without pattern-matching on message text.
    if (error.status === 423 && error.error?.blocked) {
      this.showGateBlock(error.error);
      return;
    }
    if (error.status === 422) {
      // Surface the SPECIFIC validation failures, not just the generic top-level message.
      // Backends return either { message, errors: ["...", "..."] } (controller style) or
      // { message, errors: { field: ["..."] } } (FormRequest style) — handle both, fall back to message.
      this.toastService.showError(this.extractValidationDetail(error), 'Validation Error')
      return;
    }
    if (error.status === 403) {
      this.toastService.showError(error.error.message, 'Request Error')
      return;
    }
    if (error.status === 401) {
      this.toastService.showError(error.error.message, 'Request Error')
      return;
    }

    if (error.status === 500) {
      this.toastService.showError('An error occurred at the server. Please, refresh the page and try again or contact support for assistance.', 'Server Error')
      return;
    }

    this.toastService.showError('An error occurred. Please, refresh the page and try again or contact support for assistance.', 'Unknown Error')
    return;
  }

  /**
   * The gate stop screen. A blocking modal, not a toast: a toast fades after five seconds, which is
   * fine for "this field is required" and useless for "do not let this truck in and phone the
   * office". static backdrop + keyboard:false so it cannot be dismissed by clicking away or hitting
   * escape — acknowledging is a deliberate act.
   */
  private showGateBlock(block: any): void {
    try {
      const modalRef = this.modalService.open(GateBlockModalComponent, {
        centered: true,
        backdrop: 'static',
        keyboard: false,
        size: 'lg',
      });
      modalRef.componentInstance.block = block;
    } catch (e) {
      // If the modal cannot open for any reason the gateman must STILL be told — never let a
      // rendering failure turn a refusal into silence.
      this.toastService.showError(
        (block?.message ?? 'This truck is flagged and cannot be checked in.') + ' CALL OPERATIONS.',
        'DO NOT ADMIT'
      );
    }
  }

  /**
   * Pull the specific field messages out of a 422 body (array OR {field:[msgs]}), else the message.
   *
   * Public so a screen can ALSO show the refusal inline. The toast this method feeds fades after five
   * seconds, which is right for "this field is required" and wrong for a money refusal that names a figure
   * and an action — the same reason the gate stop is a modal rather than a toast.
   */
  public extractValidationDetail(error: HttpErrorResponse): string {
    const body: any = error?.error ?? {};
    const errs = body.errors;
    let lines: string[] = [];
    if (Array.isArray(errs)) {
      lines = errs.filter((e: any) => typeof e === 'string');
    } else if (errs && typeof errs === 'object') {
      lines = Object.values(errs).reduce((acc: string[], v: any) => acc.concat(Array.isArray(v) ? v : [v]), []);
    }
    const detail = lines.filter(Boolean).join('\n');
    return detail || body.message || 'Validation failed. Please check the highlighted fields.';
  }

  private playSound(name = "tone-1.mp3") {
    let audio = new Audio();
    audio.src = "../../../assets/sounds/" + name;
    audio.load();
    audio.play();
  }

  playSoundNotification() {
    this.playSound();
  }
}
